import Decimal from 'decimal.js';

// Number grouping stays Indonesian (Rp 1.234.567,89 style) but all date text
// is rendered in consistent English short/long format (e.g. "28 Jun 2026").
const idIDNumber = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const idIDMoney  = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const idrMoney   = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const enDate     = new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
const enDateLong = new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'long', day: '2-digit' });
const enDateTime = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
});

/** Format a money amount; default currency = IDR with 'Rp ' prefix. */
export function money(value: string | number | null | undefined, currency = 'IDR'): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Decimal(typeof value === 'number' ? value.toString() : value);
  // IDR displays as whole rupiah (no fractional cents); other currencies keep 2 dp.
  if (currency === 'IDR') return `Rp ${idrMoney.format(d.toNumber())}`;
  return `${currency} ${idIDMoney.format(d.toNumber())}`;
}

/** Compact money for KPI tiles so values from Rp 0 to Rp 16 B fit one line:
 *  Rp 16,63 M · Rp 429,27 jt · Rp 850 rb. Use money() for the exact figure
 *  (e.g. in a title/tooltip). Indonesian magnitude suffixes: rb/jt/M/T. */
export function moneyCompact(value: string | number | null | undefined, currency = 'IDR'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = new Decimal(typeof value === 'number' ? value.toString() : value).toNumber();
  const prefix = currency === 'IDR' ? 'Rp ' : `${currency} `;
  const abs = Math.abs(n);
  const compact = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  if (abs >= 1e12) return `${prefix}${compact.format(n / 1e12)} T`;
  if (abs >= 1e9) return `${prefix}${compact.format(n / 1e9)} M`;
  if (abs >= 1e6) return `${prefix}${compact.format(n / 1e6)} jt`;
  if (abs >= 1e3) return `${prefix}${compact.format(n / 1e3)} rb`;
  return `${prefix}${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n)}`;
}

/** Format a quantity / non-money number. */
export function num(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Decimal(typeof value === 'number' ? value.toString() : value);
  return idIDNumber.format(d.toNumber());
}

/** Format a quantity, trimming trailing zeros (max ~2 dp): "18.800000" → "18,8", "40" → "40". */
export function qty(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Decimal(typeof value === 'number' ? value.toString() : value);
  return idIDNumber.format(d.toNumber());
}

/** Format an ISO date string as English short date, e.g. "28 Jun 2026". */
export function date(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return enDate.format(d);
}

export function dateLong(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return enDateLong.format(d);
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return enDateTime.format(d);
}

/** Truncate a long string with an ellipsis. */
export function truncate(s: string, n = 50): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// Enum tokens that read better with an explicit label than the generic
// title-case rule (hyphenation, sentence-case, or a domain term).
const ENUM_OVERRIDES: Record<string, string> = {
  straight_line:            'Straight-line',
  written_down_value:       'Written down value',
  declining_balance:        'Double declining',
  raw_material:             'Raw material',
  finished_goods:           'Finished goods',
  accumulated_depreciation: 'Accumulated Depreciation',
};

// Tokens kept upper-cased when title-casing an enum.
const ENUM_ACRONYMS = new Set([
  'ppn', 'pph', 'ppnbm', 'ar', 'ap', 'uom', 'npwp', 'vat', 'gl', 'bom',
  'hr', 'cogs', 'pos', 'usd', 'idr', 'eur', 'spb', 'grn', 'po', 'so',
  'pcr', 'wo', 'pmr', 'mr',
]);

/**
 * Humanize a snake_case / lowercase enum token for display:
 *   'straight_line' → 'Straight-line', 'raw_material' → 'Raw material',
 *   'accumulated_depreciation' → 'Accumulated Depreciation', 'male' → 'Male'.
 * Known overrides win; otherwise replace underscores with spaces, title-case
 * each word, and keep known acronyms upper-cased.
 */
export function humanizeEnum(s: string | null | undefined): string {
  if (s === null || s === undefined || s === '') return '';
  const raw = String(s).trim();
  const key = raw.toLowerCase();
  if (ENUM_OVERRIDES[key]) return ENUM_OVERRIDES[key];
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (ENUM_ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}
